import { useState } from "react";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import { Lock, Shield, Eye, EyeOff, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    const to = location.state?.from?.pathname || "/admin";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate(location.state?.from?.pathname || "/admin", { replace: true });
    } else {
      setError(res.error || "Login failed");
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black px-4 py-16 relative overflow-hidden">
      {/* ambient grid */}
      <div className="absolute inset-0 grid-lines-navy opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-[#0A0A0A] opacity-70" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-10 group">
          <div className="relative w-11 h-11 rounded-sm bg-white/5 border border-white/15 p-1.5">
            <Shield className="w-full h-full text-white" strokeWidth={1.6} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-600 border-2 border-black" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-black text-white text-sm tracking-tight uppercase">Marco Polo</span>
            <span className="font-condensed text-[10px] tracking-[0.22em] uppercase text-red-500 font-semibold">Admin · Secure</span>
          </div>
        </Link>

        <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-sm p-6 sm:p-8">
          <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Admin Login</p>
          <h1 className="font-display font-black text-3xl uppercase leading-[1]">Sign In<span className="text-red-500">.</span></h1>
          <p className="mt-3 text-slate-400 text-sm leading-relaxed">
            Access the Marco Polo Security admin dashboard. Authorized personnel only.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <label className="block">
              <span className="block font-condensed text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">
                Email <span className="text-red-500">*</span>
              </span>
              <input
                data-testid="login-email"
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="admin@marcopolosecurity.com"
              />
            </label>

            <label className="block">
              <span className="block font-condensed text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">
                Password <span className="text-red-500">*</span>
              </span>
              <div className="relative">
                <input
                  data-testid="login-password"
                  required
                  type={showPass ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(inputCls, "pr-11")}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                  aria-label="Toggle password visibility"
                  data-testid="login-toggle-visibility"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 border border-red-600/50 bg-red-600/10 text-red-300 rounded-sm px-3 py-2 text-sm"
              >
                <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="group w-full bg-white hover:bg-slate-100 disabled:opacity-60 text-black px-6 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? "Signing In…" : (<>Sign In <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>)}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-white/8 text-xs font-condensed uppercase tracking-widest text-slate-500 text-center">
            Marco Polo Security · Washington State
          </div>
        </div>

        <Link to="/" className="mt-6 block text-center font-condensed uppercase tracking-widest text-xs text-slate-500 hover:text-white">
          ← Back to Website
        </Link>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 rounded-sm px-4 py-3 h-11 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all";
